import { FilterTable } from "@/features/FilterTable/FilterTable";
import { Field } from "@/features/FilterTable/types";
import { DepField, makeFiltersBuilder } from "@/features/FilterTable/utils";
import {
  dateSchema,
  parseToDateValue,
  preprocessTextAsyncAutocompleteField,
} from "@/features/FormCard/utils";
import { createCurrencyOption } from "@/hooks/useCurrencyOption";
import { z } from "zod";

export const filterA7FIAInstrumentsSchema = z.object({
  type_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional(),
  devise_contains: z.string().optional(),
  code_contains: z.string().optional(),
  denomination_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional(),
  denomination_du_sous_jacent_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional(),
  nom_contains_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional(),
  compagnie_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional(),
  date_inactivite_contains: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
});

export type FilterA7FIAInstrumentsSchema = z.infer<
  typeof filterA7FIAInstrumentsSchema
>;

const instrumentDepFields: readonly DepField<FilterA7FIAInstrumentsSchema>[] = [
  {
    name: "denomination_contains",
    key: "denomination_du_sous_jacent",
    extract: "text",
    bucket: "equals",
  },
  {
    name: "nom_contains_contains",
    key: "nom",
    extract: "id",
    bucket: "equals",
  },
  {
    name: "compagnie_contains",
    key: "compagnie",
    extract: "text",
    bucket: "equals",
  },
];

const fields: Field[] = [
  {
    name: "type_contains",
    label: `Type`,
    type: "autocomplete",
    setOptionsFromUrl: "a7/a7_fia_instruments/filter/type",
  },
  {
    name: "devise_contains",
    label: "Devise",
    type: "autocomplete",
    options: createCurrencyOption(),
  },
  {
    name: "code_contains",
    label: "Code",
    type: "input",
  },
  {
    name: "denomination_contains",
    label: "Émetteur",
    type: "asyncAutocomplete",
    base_url: "a7/a7_fia_issuers",
    column_name: "denomination_du_sous_jacent",
    filters: makeFiltersBuilder<FilterA7FIAInstrumentsSchema>(
      instrumentDepFields,
      "denomination_contains",
    ),
  },
  {
    name: "nom_contains_contains",
    label: "Nom du fonds",
    type: "asyncAutocomplete",
    base_url: "a7/a7_fia_instruments",
    column_name: "nom",
    filters: makeFiltersBuilder<FilterA7FIAInstrumentsSchema>(
      instrumentDepFields,
      "nom_contains_contains",
    ),
  },
  {
    name: "compagnie_contains",
    label: "Compagnie",
    type: "asyncAutocomplete",
    base_url: "a7/a7_fia_instruments",
    column_name: "compagnie",
    filters: makeFiltersBuilder<FilterA7FIAInstrumentsSchema>(
      instrumentDepFields,
      "compagnie_contains",
    ),
  },
  {
    name: "date_inactivite_contains",
    label: "Date d'inactivité",
    type: "date",
  },
];

export function FilterA7FIAInstruments() {
  return (
    <FilterTable
      schema={filterA7FIAInstrumentsSchema}
      client={"a7"}
      tableName="a7_fia_instruments"
      fields={fields}
      customBuildPath={() => `a7-fia/a7_fia_instruments`}
    />
  );
}
